import { JoinTeamMutation, TeamMembershipJoinInput } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { joinTeamMutationDocument } from "./team-membership.gql";
import { EVENT_TEAM_JOIN, trackTeamEvent } from "./tracking";

export type JoinedTeamMembership = JoinTeamMutation["joinTeam"];

export interface UseJoinTeamProps {
  workspace: {
    id: string;
  };
  team: {
    id: string;
  };
  onCompleted?: (o: JoinedTeamMembership | undefined) => void;
  onError?: (e: Error) => void;
}

export const useTeamJoin = ({ onCompleted, onError, ...props }: UseJoinTeamProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: TeamMembershipJoinInput): Promise<JoinTeamMutation> => {
      const client = createOperationClient("joinTeam");
      return await client.request(joinTeamMutationDocument, { input });
    },
    onSuccess: (data) => {
      const joinTeamMembership = data?.joinTeam;
      if (joinTeamMembership) {
        trackTeamEvent(EVENT_TEAM_JOIN, joinTeamMembership);
      }

      // Invalidate specific team queries to avoid excessive refetches
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.detail(props.team.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.list(props.workspace.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.membershipList(props.team.id),
      });

      if (onCompleted) {
        onCompleted(data.joinTeam);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    queryResult: mutation,
    joinTeam: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
